import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { ToastController } from '@ionic/angular';
import { finalize } from 'rxjs';

import { GroupManagementService } from '@services/group-management.service';
import { PanelHostService } from '@services/panel-host.service';
import { TripService } from '@services/trip.service';
import { hasTripLeadership } from '../../../services/trip-access/trip-guards';
import {
  ParticipantImportRow,
  ParticipantImportStatus,
  MAX_PARTICIPANT_IMPORT_ROWS,
  countLandedEmails,
  rosterSizeChange,
  sortParticipantImport,
} from '../../../utilities/participant-import';
import { ButtonComponent } from '../../ui/button/button.component';
import { InlineAlertComponent } from '../../ui/inline-alert/inline-alert.component';
import { ContentContainerComponent } from '../../ui/content-container/content-container.component';
import { PanelBodyComponent } from '../../ui/panel-body/panel-body.component';

export const PARTICIPANT_IMPORT_TEMPLATE_URL = 'assets/participant-import-template.csv';

const STATUS_LABEL_KEYS: Record<ParticipantImportStatus, string> = {
  'will-invite': marker('group-management.panels.participant-import.status.will-invite'),
  'missing-name': marker('group-management.panels.participant-import.status.missing-name'),
  'invalid-email': marker('group-management.panels.participant-import.status.invalid-email'),
  'on-roster': marker('group-management.panels.participant-import.status.on-roster'),
  'has-trip-access': marker('group-management.panels.participant-import.status.has-trip-access'),
  duplicate: marker('group-management.panels.participant-import.status.duplicate'),
};

@Component({
  selector: 'app-participant-import',
  imports: [TranslocoDirective, ButtonComponent, ContentContainerComponent, InlineAlertComponent, PanelBodyComponent],
  templateUrl: './participant-import.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ParticipantImportComponent {
  private readonly panelHost = inject(PanelHostService);
  private readonly tripService = inject(TripService);
  private readonly groupManagementService = inject(GroupManagementService);
  private readonly transloco = inject(TranslocoService);
  private readonly toastController = inject(ToastController);

  readonly templateUrl = PARTICIPANT_IMPORT_TEMPLATE_URL;

  readonly fileName = signal<string | null>(null);
  readonly rows = signal<ParticipantImportRow[]>([]);
  readonly tooManyRows = signal(false);
  readonly maxRows = MAX_PARTICIPANT_IMPORT_ROWS;
  /** Will-invite rows the lead has left ticked. Skipped rows can never be in here. */
  readonly checked = signal<ReadonlySet<ParticipantImportRow>>(new Set());
  readonly submitting = signal(false);
  readonly dragging = signal(false);
  /** Set after a failed send once the reloaded trip shows how many of the sent rows landed. */
  readonly partialFailure = signal<{ landed: number; sent: number } | null>(null);
  /** Emails from a failed send, waiting for the trip reload to finish before counting. */
  private readonly pendingRecovery = signal<string[] | null>(null);

  readonly inviteRows = computed(() => this.rows().filter((row) => row.status === 'will-invite'));
  readonly skippedRows = computed(() => this.rows().filter((row) => row.status !== 'will-invite'));
  readonly checkedCount = computed(() => this.checked().size);
  readonly rosterSizeChange = computed(() => {
    const trip = this.tripService.currentTrip.value();
    if (!trip || !hasTripLeadership(trip)) {
      return null;
    }
    return rosterSizeChange({
      attendees: trip.attendees ?? 0,
      rosterCount: trip.roster.length,
      checkedCount: this.checkedCount(),
    });
  });

  constructor() {
    effect(() => {
      if (this.panelHost.activePanel() === 'participant-import') {
        this.resetState();
      }
    });

    effect(() => {
      const sent = this.pendingRecovery();
      if (!sent || this.tripService.currentTrip.isLoading()) {
        return;
      }
      const trip = this.tripService.currentTrip.value();
      const roster = trip && hasTripLeadership(trip) ? trip.roster : [];
      const landed = countLandedEmails(sent, roster.map((p) => p.email ?? ''));
      untracked(() => {
        this.pendingRecovery.set(null);
        this.partialFailure.set({ landed, sent: sent.length });
      });
    });
  }

  cancel(): void {
    this.panelHost.dismiss();
  }

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) {
      return;
    }
    const file = input.files?.[0];
    if (!file) {
      return;
    }
    this.loadFile(file.name, await file.text());
    // Clear so choosing the same file again still fires `change`.
    input.value = '';
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    if (!this.submitting()) {
      this.dragging.set(true);
    }
  }

  async onDrop(event: DragEvent): Promise<void> {
    event.preventDefault();
    this.dragging.set(false);
    const file = event.dataTransfer?.files[0];
    if (!file || this.submitting()) {
      return;
    }
    this.loadFile(file.name, await file.text());
  }

  loadFile(fileName: string, text: string): void {
    const trip = this.tripService.currentTrip.value();
    if (!trip || !hasTripLeadership(trip)) {
      return;
    }
    const result = sortParticipantImport(text, {
      rosterEmails: trip.roster.map((p) => p.email ?? ''),
      nonRosterEmails: trip.nonRosterUsers.map((p) => p.email ?? ''),
    });
    this.fileName.set(fileName);
    this.rows.set(result.rows);
    this.tooManyRows.set(result.tooManyRows);
    this.checked.set(new Set(result.rows.filter((row) => row.status === 'will-invite')));
  }

  inviteButtonLabel(): string {
    return this.countLabel(
      marker('group-management.panels.participant-import.invite-button-one'),
      marker('group-management.panels.participant-import.invite-button'),
    );
  }

  sendingLabel(): string {
    return this.countLabel(
      marker('group-management.panels.participant-import.sending-button-one'),
      marker('group-management.panels.participant-import.sending-button'),
    );
  }

  private countLabel(oneKey: string, manyKey: string): string {
    const count = this.checkedCount();
    return this.transloco.translate(count === 1 ? oneKey : manyKey, { count });
  }

  statusLabel(row: ParticipantImportRow): string {
    return this.transloco.translate(STATUS_LABEL_KEYS[row.status]);
  }

  isChecked(row: ParticipantImportRow): boolean {
    return this.checked().has(row);
  }

  toggle(row: ParticipantImportRow): void {
    if (row.status !== 'will-invite') {
      return;
    }
    const next = new Set(this.checked());
    if (next.has(row)) {
      next.delete(row);
    } else {
      next.add(row);
    }
    this.checked.set(next);
  }

  submit(): void {
    const trip = this.tripService.currentTrip.value();
    const selected = this.inviteRows().filter((row) => this.checked().has(row));
    if (!trip || selected.length === 0 || this.submitting()) {
      return;
    }

    this.submitting.set(true);
    this.partialFailure.set(null);
    this.panelHost.registerDirtyPredicate(() => this.submitting(), {
      header: this.transloco.translate(marker('group-management.panels.participant-import.close-while-sending.title')),
      message: this.transloco.translate(
        marker('group-management.panels.participant-import.close-while-sending.message'),
      ),
      cancelText: this.transloco.translate(
        marker('group-management.panels.participant-import.close-while-sending.cancel'),
      ),
      confirmText: this.transloco.translate(
        marker('group-management.panels.participant-import.close-while-sending.confirm'),
      ),
    });
    this.groupManagementService
      .addParticipants(
        trip.id,
        selected.map(({ firstname, lastname, email }) => ({ firstname, lastname, email })),
      )
      .pipe(
        finalize(() => {
          this.submitting.set(false);
          this.panelHost.clearDirtyPredicate();
        }),
      )
      .subscribe({
        next: async () => {
          const toast = await this.toastController.create({
            message: this.transloco.translate(
              selected.length === 1
                ? marker('group-management.panels.participant-import.success-one')
                : marker('group-management.panels.participant-import.success'),
              { count: selected.length },
            ),
            duration: 4000,
            color: 'success',
          });
          await toast.present();
          this.panelHost.dismiss();
        },
        error: () => {
          // No transaction server-side: rows before the failure are already added and invited.
          this.tripService.currentTrip.reload();
          this.pendingRecovery.set(selected.map((row) => row.email));
        },
      });
  }

  private resetState(): void {
    this.fileName.set(null);
    this.rows.set([]);
    this.tooManyRows.set(false);
    this.checked.set(new Set());
    this.submitting.set(false);
    this.partialFailure.set(null);
    this.pendingRecovery.set(null);
  }
}
